/**
 * @file Firestore listeners owned by the controlled-drugs domain.
 */

import {
    collection,
    limit,
    onSnapshot,
    orderBy,
    query,
    where,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { db } from "../../app/firebase/firebase.js";
import { activeListeners, appData, state } from "../../app/state/state.js";

const ACTIVE_APPROVAL_STATUSES = ["pending", "escalated"];

function rerenderDrugsView(errorLabel) {
    import("../../app/view/viewController.js").then((module) => {
        if (module.renderCurrentBoard) {
            module.renderCurrentBoard();
        }
    }).catch((err) => {
        console.error(errorLabel, err);
    });
}

/**
 * Attaches controlled-drug listeners available to the current user.
 */
export function attachDrugDataListeners() {
    const inventoryQuery = query(collection(db, "drug_inventory"));
    const unsubInventory = onSnapshot(inventoryQuery, (snapshot) => {
        appData.drugInventory = snapshot.docs.map((snapshotDoc) => ({
            id: snapshotDoc.id,
            ...snapshotDoc.data(),
        }));
        if (state.currentView === "drugs") {
            rerenderDrugsView("Error updating drugs view:");
        }
    }, (error) => {
        console.error("Error listening to drug inventory:", error);
        appData.drugInventory = [];
    });
    activeListeners.push(unsubInventory);

    const rolesQuery = query(collection(db, "drug_roles"));
    const unsubRoles = onSnapshot(rolesQuery, (snapshot) => {
        appData.drugRoles = snapshot.docs.map((snapshotDoc) => ({
            id: snapshotDoc.id,
            ...snapshotDoc.data(),
        }));
    }, (error) => {
        console.error("Error listening to drug roles:", error);
        appData.drugRoles = [];
    });
    activeListeners.push(unsubRoles);

    const isAdmin = state.currentUserProfile?.role === "admin";
    const drugPerms = state.currentUserProfile?.drugPermissions || {};
    const hasAnyDrugAccess = isAdmin || Object.values(drugPerms).some(val => val === true);
    const canViewHistory = isAdmin || drugPerms.viewHistory === true || drugPerms.drugAdmin === true;

    if (canViewHistory) {
        const transactionsQuery = query(
            collection(db, "drug_transactions"),
            orderBy("createdAt", "desc"),
            limit(50),
        );
        const unsubTrans = onSnapshot(transactionsQuery, (snapshot) => {
            appData.drugTransactions = snapshot.docs.map((snapshotDoc) => ({
                id: snapshotDoc.id,
                ...snapshotDoc.data(),
            }));
            if (!snapshot.empty) {
                state.drugHistoryPagination.lastDoc =
                    snapshot.docs[snapshot.docs.length - 1];
                state.drugHistoryPagination.hasMore =
                    snapshot.docs.length === 50;
            } else {
                state.drugHistoryPagination.lastDoc = null;
                state.drugHistoryPagination.hasMore = false;
            }
            if (state.currentView === "drugs" &&
                state.drugsView === "history") {
                rerenderDrugsView("Error updating drugs history:");
            }
        }, (error) => {
            console.error("Error listening to drug transactions:", error);
            appData.drugTransactions = [];
        });
        activeListeners.push(unsubTrans);

        const approvalsQuery = query(
            collection(db, "approvals"),
            where("domain", "==", "drugs"),
            where("status", "in", ACTIVE_APPROVAL_STATUSES),
        );
        const unsubApprovals = onSnapshot(approvalsQuery, (snapshot) => {
            appData.allPendingApprovals = snapshot.docs.map((snapshotDoc) => ({
                id: snapshotDoc.id,
                ...snapshotDoc.data(),
            }));
            if (state.currentView === "drugs" && state.drugsView === "history") {
                rerenderDrugsView("Error updating drugs history (approvals):");
            }
        }, (error) => {
            console.error("Error listening to pending approvals:", error);
            appData.allPendingApprovals = [];
        });
        activeListeners.push(unsubApprovals);
    }

    if (canViewHistory) {
        const ledgerQuery = query(
            collection(db, "drug_ledger"),
            orderBy("createdAt", "desc"),
            limit(1000),
        );
        const unsubLedger = onSnapshot(ledgerQuery, (snapshot) => {
            appData.drugLedger = snapshot.docs.map((snapshotDoc) => ({
                id: snapshotDoc.id,
                ...snapshotDoc.data(),
            }));
        }, (error) => {
            console.error("Error listening to drug ledger:", error);
            appData.drugLedger = [];
        });
        activeListeners.push(unsubLedger);
    }

    if (hasAnyDrugAccess) {
        const auditsQuery = query(
            collection(db, "drug_audits"),
            orderBy("startedAt", "desc"),
        );
        const unsubAudits = onSnapshot(auditsQuery, (snapshot) => {
            appData.drugAudits = snapshot.docs.map((snapshotDoc) => ({
                id: snapshotDoc.id,
                ...snapshotDoc.data(),
            }));

            const previousActiveAudit = appData.activeAudit;
            appData.activeAudit = appData.drugAudits.find(
                (a) => a.status === "in_progress",
            ) || null;

            if (state.currentView === "drugs") {
                const auditIdChanged = previousActiveAudit?.id !== appData.activeAudit?.id;
                const auditStatusChanged = previousActiveAudit?.status !== appData.activeAudit?.status;
                const auditStateToggle = (!!previousActiveAudit) !== (!!appData.activeAudit);

                if (auditStateToggle || auditIdChanged || auditStatusChanged) {
                    rerenderDrugsView("Error updating drugs view:");
                } else {
                    import("./renderDrugs.js").then((module) => {
                        if (module.syncAuditConsoleData) {
                            module.syncAuditConsoleData();
                        }
                    }).catch((err) => {
                        console.error("Error triggering silent audit sync:", err);
                    });
                }
            }
        }, (error) => {
            console.error("Error listening to drug audits:", error);
            appData.drugAudits = [];
            appData.activeAudit = null;
        });
        activeListeners.push(unsubAudits);
    }
}
